import { useState } from "react";

export const DEFAULT_MIN_COLUMN_WIDTH = 72;

export function buildResizableGridTemplate<
  TId extends string,
  TCol extends { id: TId; width: string },
>(columns: readonly TCol[], widths: Partial<Record<TId, number>> = {}): string {
  return columns.map((c) => (widths[c.id] ? `${widths[c.id]}px` : c.width)).join(" ");
}

export function resizeHandlerFor<TId extends string>(
  index: number,
  total: number,
  onResizeColumn?: (id: TId, px: number) => void,
): ((id: TId, px: number) => void) | undefined {
  return index < total - 1 ? onResizeColumn : undefined;
}

export function ColumnResizeHandle<TId extends string>({
  columnId,
  onResize,
  minWidth = DEFAULT_MIN_COLUMN_WIDTH,
}: {
  columnId: TId;
  onResize: (id: TId, px: number) => void;
  minWidth?: number;
}) {
  const [drag, setDrag] = useState<{
    x: number;
    width: number;
    neighborId: TId | null;
    neighborWidth: number;
  } | null>(null);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const cell = e.currentTarget.parentElement;
    const width = cell?.getBoundingClientRect().width ?? minWidth;
    const neighbor = cell?.nextElementSibling as HTMLElement | null;
    const neighborId = (neighbor?.getAttribute("data-column-id") ?? null) as TId | null;
    const neighborWidth = neighbor?.getBoundingClientRect().width ?? 0;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ x: e.clientX, width, neighborId, neighborWidth });
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!drag) return;
    const { x, width, neighborId, neighborWidth } = drag;
    const requested = e.clientX - x;
    const delta = neighborId
      ? Math.max(minWidth - width, Math.min(requested, neighborWidth - minWidth))
      : Math.max(minWidth - width, requested);
    onResize(columnId, Math.round(width + delta));
    if (neighborId) onResize(neighborId, Math.round(neighborWidth - delta));
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setDrag(null);
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="absolute inset-y-0 -right-1 z-10 flex w-2 cursor-col-resize touch-none justify-center select-none"
    >
      <span
        className={["h-full w-px", drag ? "bg-green-500" : "bg-transparent hover:bg-grey-300"].join(
          " ",
        )}
      />
    </div>
  );
}
